// Syntax highlighting for ```lang code blocks, like Discord. highlight.js loads on first use (its own chunk), so
// chat's cold start never pays for it. Its output is turned into React elements (only <span class="hljs-…">), never
// set as HTML.
import { createElement, useEffect, useState, type ReactNode } from "react";
import type { HLJSApi } from "highlight.js";

let loading: Promise<HLJSApi> | null = null;
let ready: HLJSApi | null = null;

function load(): Promise<HLJSApi> {
  loading ??= (async () => {
    const [core, ...langs] = await Promise.all([
      import("highlight.js/lib/core"),
      import("highlight.js/lib/languages/javascript"),
      import("highlight.js/lib/languages/typescript"),
      import("highlight.js/lib/languages/python"),
      import("highlight.js/lib/languages/json"),
      import("highlight.js/lib/languages/bash"),
      import("highlight.js/lib/languages/shell"),
      import("highlight.js/lib/languages/css"),
      import("highlight.js/lib/languages/xml"),
      import("highlight.js/lib/languages/sql"),
      import("highlight.js/lib/languages/go"),
      import("highlight.js/lib/languages/rust"),
      import("highlight.js/lib/languages/java"),
      import("highlight.js/lib/languages/c"),
      import("highlight.js/lib/languages/cpp"),
      import("highlight.js/lib/languages/csharp"),
      import("highlight.js/lib/languages/diff"),
      import("highlight.js/lib/languages/yaml"),
      import("highlight.js/lib/languages/markdown"),
      import("highlight.js/lib/languages/ruby"),
      import("highlight.js/lib/languages/php"),
      import("highlight.js/lib/languages/swift"),
      import("highlight.js/lib/languages/kotlin"),
      import("highlight.js/lib/languages/ini"),
      import("highlight.js/lib/languages/dockerfile"),
      import("highlight.js/lib/languages/lua"),
    ]);
    const names = ["javascript", "typescript", "python", "json", "bash", "shell", "css", "xml", "sql", "go", "rust", "java", "c", "cpp", "csharp", "diff", "yaml", "markdown", "ruby", "php", "swift", "kotlin", "ini", "dockerfile", "lua"];
    const hljs = core.default;
    langs.forEach((l, k) => hljs.registerLanguage(names[k]!, l.default));
    // Names people type that the definitions don't already alias.
    hljs.registerAliases(["jsx", "mjs", "cjs"], { languageName: "javascript" });
    hljs.registerAliases(["toml"], { languageName: "ini" });
    hljs.registerAliases(["sh", "zsh"], { languageName: "bash" });
    ready = hljs;
    return hljs;
  })();
  return loading;
}

/** highlight.js's HTML (escaped text in <span class>) → React nodes. Anything that isn't a span becomes its text. */
function toNodes(html: string): ReactNode[] {
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, "text/html");
  let key = 0;
  const walk = (el: ParentNode): ReactNode[] =>
    [...el.childNodes].map((n) => {
      if (n.nodeType === Node.TEXT_NODE) return n.textContent;
      if (n instanceof HTMLSpanElement) {
        const cls = [...n.classList].filter((c) => /^hljs-[\w-]+$|^[a-z_]+_$/.test(c)).join(" ");
        return createElement("span", { key: key++, className: cls || undefined }, ...walk(n));
      }
      return n.textContent;
    });
  return walk(doc.body);
}

const cache = new Map<string, ReactNode[] | null>();

/** Highlighted code, or null (unknown language, or still loading: the plain text shows meanwhile). */
export function useHighlight(code: string, lang: string | null): ReactNode[] | null {
  const key = `${lang}\u0000${code}`;
  const known = (h: HLJSApi | null) => !!lang && !!h?.getLanguage(lang);
  const compute = (h: HLJSApi) => {
    if (cache.has(key)) return cache.get(key)!;
    const nodes = known(h) ? toNodes(h.highlight(code, { language: lang!, ignoreIllegals: true }).value) : null;
    if (cache.size > 500) cache.clear();
    cache.set(key, nodes);
    return nodes;
  };
  const [nodes, setNodes] = useState<ReactNode[] | null>(() => (ready && lang ? compute(ready) : null));
  useEffect(() => {
    if (!lang) return setNodes(null);
    let live = true;
    void load().then((h) => live && setNodes(compute(h)));
    return () => {
      live = false;
    };
  }, [key]);
  return nodes;
}
